<template>
  <div class='w-full h-full bg-white flex flex-col px-8'>
    <div class="flex items-center justify-between py-8">
      <h2 class="font-semibold">Admin</h2>
      <div v-if="[ORGANIZATION_STATE_TYPES.PENDING_ONBOARDING, ORGANIZATION_STATE_TYPES.TRIAL].includes(organizationState)"
        class="flex gap-10">
          <busy-button id="setStateToTrialButton"
            variant="bordered"
            :is-loading="isSetStateToTrialLoading"
            :is-disabled="isLoading"
            class="w-64"
            @click="setOrganizationStateToTrial"
          >
            {{ getSetOrganizationStateToTrialButtonLabel() }}
          </busy-button>
          <busy-button id="setStateToPaidButton"
            variant="primary"
            :is-loading="isSetStateToPaidLoading"
            :is-disabled="isLoading"
            class="w-64"
            @click="setOrganizationStateToPaid"
          >
            Set Organization State to Paid
          </busy-button>
        </div>
        <busy-button v-if="organizationState === ORGANIZATION_STATE_TYPES.PAID && isOrganizationActive"
          id="deactivateOrganizationButton"
          variant="error"
          :is-loading="isLoading"
          :is-disabled="isLoading"
          class="w-64"
          @click="deactivateOrganization"
        >
          Deactivate Organization
        </busy-button>
    </div>

    <div class="flex flex-col gap-6 text-gray-dark">
      <div class="flex flex-col gap-1">
        <label class="font-bold">Organization ID</label>
        <span>{{ organizationId }}</span>
      </div>

      <div class="flex flex-col gap-1">
        <label class="font-bold">Organization Name</label>
        <span>{{ organizationName }}</span>
      </div>

      <div class="flex flex-col gap-1">
        <label class="font-bold">Organization State</label>
        <span>{{ organizationStateString }}</span>
      </div>

      <div v-if="organizationState === 'trial'" class="flex flex-col gap-1">
        <label class="font-bold">Trial Status</label>
        <span v-if="remainingTrialDays > 0">{{ getTrialStatusString() }}</span>
        <span v-else>Trial expired</span>
      </div>
    </div>
  </div>
</template>

<script>
import organizationService from '@/services/amio/organization.service'
import moment from 'moment'
import BusyButton from '@/components/common/BusyButton'
import toaster from '@/utils/toaster'

const ORGANIZATION_STATE_TYPES = {
  PENDING_ONBOARDING: 'pending_onboarding',
  TRIAL: 'trial',
  PAID: 'paid'
}

export default {
  name: 'AdminOrganization',

  components: {
    BusyButton
  },

  data() {
    return {
      isLoading: false,
      organization: null,
      remainingTrialDays: 0,
      trialEndDate: null,
      ORGANIZATION_STATE_TYPES
    }
  },

  computed: {
    organizationId() {
      return this.organization?.id
    },
    organizationName() {
      return this.organization?.name
    },
    organizationState() {
      return this.organization?.state
    },
    isOrganizationActive() {
      return this.organization?.isActive
    },
    organizationStateString() {
      if (this.organizationState === ORGANIZATION_STATE_TYPES.PENDING_ONBOARDING) {
        return 'Pending Onboarding'
      }

      const activeString = this.isOrganizationActive ? 'Active' : 'Deactivated'
      return this.organizationState === ORGANIZATION_STATE_TYPES.TRIAL ?
        `Trial (${activeString})` :
        `Paid (${activeString})`
    }
  },

  methods: {
    getSetOrganizationStateToTrialButtonLabel() {
      return this.organizationState === ORGANIZATION_STATE_TYPES.PENDING_ONBOARDING ?
        'Set Organization State to Trial' :
        'Extend Organization Trial'
    },

    getTrialStatusString() {
      if (!this.trialEndDate) return null

      const trialEndDate = this.trialEndDate.format('dddd, DD MMM YYYY')
      return `Trial active until ${trialEndDate} (${this.remainingTrialDays} days remaining)`
    },

    calculateRemainingTrialDays() {
      this.trialEndDate = moment(this.organization.createdOn).add(2, 'weeks')
      this.remainingTrialDays = Math.max(this.trialEndDate.diff(moment(), 'days'), 0)
    },

    async setOrganizationStateToTrial() {
      this.isLoading = true
      this.isSetStateToTrialLoading = true
      try {
        this.organization = await organizationService.setState(ORGANIZATION_STATE_TYPES.TRIAL)
        this.calculateRemainingTrialDays()
      } catch (error) {
        toaster.error(error)
      } finally {
        this.isLoading = false
        this.isSetStateToTrialLoading = false
      }
    },

    async setOrganizationStateToPaid() {
      this.isLoading = true
      this.isSetStateToPaidLoading = true
      try {
        const organization = await organizationService.setState(ORGANIZATION_STATE_TYPES.PAID)
        await new Promise(resolve => setTimeout(resolve, 3000))
        this.organization = organization
      } catch (error) {
        toaster.error(error)
      } finally {
        this.isLoading = false
        this.isSetStateToPaidLoading = false
      }
    },

    async deactivateOrganization() {
      this.isLoading = true
      try {
        await organizationService.deactivate()
        this.organization.isActive = false
      } catch (error) {
        toaster.error(error)
      } finally {
        this.isLoading = false
      }
    }
  },

  async mounted() {
    this.organization = await organizationService.get()
    if (this.organization.state === 'trial') {
      this.calculateRemainingTrialDays()
    }
  }
}
</script>